import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, onAccessTokenChange, refreshSession, setAccessToken } from "../lib/api";
import type { LoginOutcome, LoginResponse, MeResponse, PlatformRole, SessionUser, TotpChallenge } from "../lib/types";

/**
 * `forbidden` is its own state, distinct from `anonymous`: an ordinary customer
 * who signs in here has valid credentials but no business being in the console.
 * Bouncing them to the login form would just invite them to try again.
 */
type Status = "loading" | "authenticated" | "anonymous" | "forbidden";

interface AuthContextValue {
  status: Status;
  user: SessionUser | null;
  role: PlatformRole | null;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  /** Completes a challenged login with the second factor. */
  verifyTwoFactor: (challengeToken: string, code: string) => Promise<void>;
  /** Re-fetches the session, e.g. after 2FA enrollment or disabling. */
  reloadSession: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);

  const adopt = useCallback((next: SessionUser | null) => {
    setUser(next);
    setStatus(next?.platformRole ? "authenticated" : next ? "forbidden" : "anonymous");
  }, []);

  const loadSession = useCallback(async () => {
    const session = await api<MeResponse>("/v1/auth/me");
    adopt(session.user);
  }, [adopt]);

  /**
   * A refresh that fails clears the token from under us. Without this the
   * console stays "authenticated" over a session that no longer exists, and
   * every panel renders its own 401 instead of the login form.
   *
   * Only the null direction is handled here: arriving at a token goes through
   * `adopt`, which has to weigh the platform role before it can say the
   * operator is in.
   */
  useEffect(() => onAccessTokenChange((token) => {
    if (token === null) adopt(null);
  }), [adopt]);

  // On boot the access token is gone but the refresh cookie may not be.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const token = await refreshSession();
      if (cancelled) return;
      if (!token) {
        setStatus("anonymous");
        return;
      }
      try {
        await loadSession();
      } catch {
        if (!cancelled) setStatus("anonymous");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      const result = await api<LoginResponse | TotpChallenge>("/v1/auth/login", {
        method: "POST",
        body: { email, password },
        anonymous: true,
      });
      if ("challenge" in result) {
        return {
          kind: "challenge",
          challengeToken: result.challengeToken,
          expiresIn: result.expiresIn,
        };
      }
      setAccessToken(result.accessToken);
      adopt(result.user);
      return { kind: "authenticated" };
    },
    [adopt],
  );

  const verifyTwoFactor = useCallback(
    async (challengeToken: string, code: string) => {
      const result = await api<LoginResponse>("/v1/auth/2fa/verify", {
        method: "POST",
        body: { challengeToken, code },
        anonymous: true,
      });
      setAccessToken(result.accessToken);
      adopt(result.user);
    },
    [adopt],
  );

  const logout = useCallback(async () => {
    try {
      await api<void>("/v1/auth/logout", { method: "POST" });
    } finally {
      setAccessToken(null);
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, role: user?.platformRole ?? null, login, verifyTwoFactor, reloadSession: loadSession, logout }),
    [status, user, login, verifyTwoFactor, loadSession, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}

/**
 * True only for superadmins. Support staff see the whole platform but cannot
 * change it — hiding a control they cannot use beats showing them a 403, and the
 * API enforces the same rule regardless of what the UI renders.
 */
export function useCanWrite(): boolean {
  return useAuth().role === "superadmin";
}
